import { resolveSchema } from '@jsonforms/core';
import type { JsonSchema } from '@jsonforms/core';
import { isEqual, isPlainObject } from 'es-toolkit';

type SchemaNode = Record<string, unknown>;

interface ConditionalRule {
    if: SchemaNode;
    then?: SchemaNode;
    else?: SchemaNode;
}

const isNode = (value: unknown): value is SchemaNode => isPlainObject(value);

const resolve = (node: SchemaNode, root: JsonSchema): unknown =>
    typeof node.$ref === 'string' ? resolveSchema(node as JsonSchema, '', root) : node;

// Evaluates the JSON Schema subset used by order conditional rules against data, resolving $ref at every level
const matches = (rawCondition: unknown, data: unknown, root: JsonSchema): boolean => {
    const condition = isNode(rawCondition) ? resolve(rawCondition, root) : rawCondition;
    if (!isNode(condition)) {
        return true;
    }
    if ('const' in condition && !isEqual(data, condition.const)) {
        return false;
    }
    if (Array.isArray(condition.enum) && !condition.enum.includes(data)) {
        return false;
    }
    if (Array.isArray(condition.required)) {
        if (!isNode(data) || condition.required.some((key) => data[String(key)] === undefined)) {
            return false;
        }
    }
    if (isNode(condition.properties)) {
        if (!isNode(data)) {
            return false;
        }
        for (const [key, sub] of Object.entries(condition.properties)) {
            if (data[key] !== undefined && !matches(sub, data[key], root)) {
                return false;
            }
        }
    }
    if (Array.isArray(condition.allOf) && !condition.allOf.every((sub) => matches(sub, data, root))) {
        return false;
    }
    if (Array.isArray(condition.anyOf) && !condition.anyOf.some((sub) => matches(sub, data, root))) {
        return false;
    }
    if ('not' in condition && matches(condition.not, data, root)) {
        return false;
    }
    return true;
};

const collectConditionalRules = (node: unknown, root: JsonSchema, rules: ConditionalRule[] = []): ConditionalRule[] => {
    if (!isNode(node)) {
        return rules;
    }
    const resolved = resolve(node, root);
    if (!isNode(resolved)) {
        return rules;
    }
    if (isNode(resolved.if)) {
        rules.push({
            if: resolved.if,
            then: isNode(resolved.then) ? resolved.then : undefined,
            else: isNode(resolved.else) ? resolved.else : undefined,
        });
    }
    if (Array.isArray(resolved.allOf)) {
        resolved.allOf.forEach((item) => collectConditionalRules(item, root, rules));
    }
    return rules;
};

// Every `not` subschema in a branch, including nested allOf entries and resolved refs
const collectNots = (node: unknown, root: JsonSchema, nots: SchemaNode[] = []): SchemaNode[] => {
    if (!isNode(node)) {
        return nots;
    }
    const resolved = resolve(node, root);
    if (!isNode(resolved)) {
        return nots;
    }
    if (isNode(resolved.not)) {
        nots.push(resolved.not);
    }
    if (Array.isArray(resolved.allOf)) {
        resolved.allOf.forEach((item) => collectNots(item, root, nots));
    }
    return nots;
};

/**
 * Whether any conditional rule of the schema forbids `property` for the given data: the property is
 * forbidden when its presence alone makes a `not` subschema of the active branch match.
 */
export const isPropertyForbiddenBySchema = (schema: JsonSchema, data: unknown, property: string): boolean => {
    const base = isNode(data) ? data : {};
    const { [property]: current, ...without } = base;
    const withProperty = { ...without, [property]: current ?? null };

    return collectConditionalRules(schema, schema).some((rule) => {
        const branch = matches(rule.if, data, schema) ? rule.then : rule.else;
        return collectNots(branch, schema).some(
            (not) => matches(not, withProperty, schema) && !matches(not, without, schema),
        );
    });
};
